"use client";

// Form
import { c as _c } from "react/compiler-runtime";
import { Button, ButtonVariant, Checkbox, Input, Text, TextColor, TextSize, TextVariant, TextWeight } from "@workspace/ui/components";
import { useMatchBreakpoints } from "@workspace/ui/hooks";
import { Form, Formik } from "formik";
import React, { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import styled from "styled-components";
import { useGlobal } from "../../context";
import validationSchema from "./validationSchema";
// Skeleton Component - bez zmian
export const NewsletterFormSkeleton = t0 => {
  const $ = _c(10);
  const {
    modalVersion: t1
  } = t0;
  const modalVersion = t1 === undefined ? false : t1;
  const t2 = `${modalVersion ? "" : "my-16 mx-auto max-w-2xl"}`;
  const t3 = `${modalVersion ? "px-6 py-12" : "px-20 py-16"}`;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex flex-col gap-2 mb-8 text-center"><div className="h-4 bg-gray-200 rounded w-48 mx-auto mb-2 animate-pulse" /><div className="h-5 bg-gray-200 rounded w-72 mx-auto animate-pulse" /></div>;
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex gap-2 justify-center items-center mb-4"><div className="h-12 bg-gray-200 rounded w-full max-w-[450px] animate-pulse" /></div>;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="mb-4 flex items-center justify-center"><div className="h-4 w-4 bg-gray-200 rounded mr-2 animate-pulse" /><div className="h-4 bg-gray-200 rounded w-64 animate-pulse" /></div>;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex flex-col justify-center gap-2 items-start w-full mb-4"><div className="h-3 bg-gray-200 rounded w-80 mx-auto animate-pulse" /><div className="h-3 bg-gray-200 rounded w-72 mx-auto mb-4 animate-pulse" /><div className="h-3 bg-gray-200 rounded w-96 max-w-[450px] mx-auto animate-pulse" /></div>;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex justify-center items-center mt-6"><div className="h-10 bg-gray-200 rounded w-32 animate-pulse" /></div>;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  let t9;
  if ($[5] !== t3) {
    t9 = <div className={t3}>{t4}{t5}{t6}{t7}{t8}</div>;
    $[5] = t3;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  let t10;
  if ($[7] !== t2 || $[8] !== t9) {
    t10 = <NewsletterFormWrapper className={t2}>{t9}</NewsletterFormWrapper>;
    $[7] = t2;
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  return t10;
};
const NewsletterForm = ({
  modalVersion,
  unconfirmed = false
}) => {
  const [status, setStatus] = useState({
    unauthorized: false,
    success: false
  });
  const {
    isXs,
    isSm,
    isMd
  } = useMatchBreakpoints();
  const isMobile = isXs || isSm || isMd;
  const {
    createGetResponseSubscribeData
  } = useGlobal();
  const formRef = useRef(null);
  const recaptchaRef = useRef(null);

  // InitialValues
  const initialValues = {
    email: "",
    terms: false,
    recaptcha: null
  };
  const formSchema = validationSchema();
  const statusContent = {
    unauthorized: "Nie udało się wysłać wiadomości",
    success: "Subskrypcja została zapisana, zatwierdź ją za pomocą linku wysłanego na adres email"
  };
  const handleRecaptchaChange = (token, setFieldValue) => {
    setFieldValue("recaptcha", token);
  };
  const executeRecaptcha = async () => {
    return new Promise((resolve, reject) => {
      if (!recaptchaRef.current) {
        reject(new Error("ReCAPTCHA nie jest dostępna"));
        return;
      }
      try {
        recaptchaRef.current.executeAsync().then(token_0 => {
          if (token_0) {
            resolve(token_0);
          } else {
            reject(new Error("Nie otrzymano tokenu reCAPTCHA"));
          }
        }).catch(error_0 => {
          console.error("Błąd executeAsync:", error_0);
          reject(new Error("Weryfikacja reCAPTCHA nie powiodła się"));
        });
      } catch (error) {
        console.error("Błąd synchroniczny reCAPTCHA:", error);
        reject(new Error("Błąd wykonywania reCAPTCHA"));
      }
    });
  };
  const resetRecaptcha = () => {
    try {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error_1) {
      console.error("Błąd resetowania reCAPTCHA:", error_1);
    }
  };
  const handleSubmit = async (values, {
    setSubmitting,
    setFieldError,
    resetForm
  }) => {
    try {
      setSubmitting(true);

      // Reset poprzednich statusów
      setStatus({
        unauthorized: false,
        success: false
      });

      // Wykonaj weryfikację reCAPTCHA
      let recaptchaToken = values.recaptcha;
      if (!recaptchaToken) {
        try {
          recaptchaToken = await executeRecaptcha();
        } catch (recaptchaError) {
          console.error("Błąd reCAPTCHA:", recaptchaError);
          setFieldError("recaptcha", "Weryfikacja reCAPTCHA nie powiodła się. Spróbuj ponownie.");
          return;
        }
      }

      // Wyślij dane do GetResponse
      try {
        const response = await createGetResponseSubscribeData(values.email);
        console.log("GetResponse response:", response);

        // Sprawdź czy odpowiedź jest poprawna
        if (response && response.code !== 200) {
          throw new Error(response.data.message);
        }
        setStatus({
          unauthorized: false,
          success: true
        });
        resetForm();
        resetRecaptcha();
      } catch (submitError) {
        console.error("Błąd wysyłania do GetResponse:", submitError);
        throw submitError;
      }
    } catch (error_2) {
      console.error("Błąd wysyłania formularza:", error_2);
      setStatus({
        unauthorized: true,
        success: false
      });
      resetRecaptcha();
    } finally {
      setSubmitting(false);
    }
  };
  const handleRecaptchaError = error_3 => {
    console.error("ReCAPTCHA onError:", error_3);
    if (formRef.current) {
      formRef.current.setFieldValue("recaptcha", null);
    }
  };
  const handleRecaptchaExpired = () => {
    console.log("ReCAPTCHA expired");
    if (formRef.current) {
      formRef.current.setFieldValue("recaptcha", null);
    }
  };
  return <Formik initialValues={initialValues} validationSchema={formSchema} onSubmit={handleSubmit} innerRef={formRef}>
      {({
      values: values_0,
      touched,
      errors,
      setFieldValue: setFieldValue_0,
      isSubmitting
    }) => <NewsletterFormWrapper className={`${modalVersion ? "" : "my-16 mx-auto max-w-2xl"}`}>
          {Object.values(status).indexOf(true) > -1 && <InfoBar $error={status.unauthorized} $success={status.success}>
              <Text variant={TextVariant.P} text={Object.values(statusContent)[Object.values(status).indexOf(true)]} color={status.success ? TextColor.SPECIAL_700 : TextColor.ERROR_500} />
            </InfoBar>}

          <FormWrapper className={`${modalVersion ? "px-6 py-12" : "px-10 py-8 md:px-20 md:py-16"}`}>
            {unconfirmed ? <Text variant={TextVariant.P} text="Subskrypcja została zapisana, zatwierdź ją za pomocą linku wysłanego na adres email" color={TextColor.GRAYSCALE_700} className="text-center" /> : <>
                <div className="flex flex-col gap-2 mb-8 text-center">
                  <Text text="Zapisz się do newslettera" weight={TextWeight.SEMIBOLD} textTransform="uppercase" className="tracking-widest" color={TextColor.BLACK} fontSize={isMobile ? TextSize.XXS : TextSize.XS} variant={isMobile ? TextVariant.H5 : TextVariant.H4} />

                  <Text text="Poznaj modowe trendy i bądź na bieżąco z akcjami i promocjami!" variant={TextVariant.P} fontSize={TextSize.S} weight={TextWeight.BOLD} color={TextColor.GRAYSCALE_600} className="tracking-wide" />
                </div>

                <div className="flex gap-2 justify-center items-center">
                  <Input id="email" value={values_0.email} name="email" placeholder="Twój adres email" focused={touched.email} validated={typeof errors.email === "undefined" && touched.email} error={typeof errors.email !== "undefined" && touched.email} errorHelper={errors.email} onChange={e => setFieldValue_0("email", e.target.value)} icon={false} type="text" autoComplete="off" className="max-w-[450px]" />
                </div>

                <CheckboxWrapper className="mb-4">
                  <Checkbox id="terms" name="terms" label="" labelHtml="Akceptuję <a href='/regulamin-sklepu' target='_blank'>warunki newslettera</a> oraz <a href='/polityka-prywatnosci' target='_blank'>polityką prywatności</a> <span className='text-red-500'>*</span>" checked={values_0.terms} onChange={() => setFieldValue_0("terms", !values_0.terms)} />

                  {typeof errors.terms !== "undefined" && touched.terms && <ErrorFormHelperText className="absolute !bottom-1 left-[50%] translate-x-[-50%] w-full">
                      <Text text={errors.terms} color={TextColor.ERROR_500} fontSize={TextSize.XS} />
                    </ErrorFormHelperText>}
                </CheckboxWrapper>

                {/* Wyświetl błąd reCAPTCHA jeśli wystąpi */}
                {typeof errors.recaptcha !== "undefined" && <div className="mb-4 text-center">
                    <Text text={errors.recaptcha} color={TextColor.ERROR_500} fontSize={TextSize.XS} />
                  </div>}

                <div className="flex flex-col justify-center gap-2 items-start w-full mb-4">
                  <Text text="• zwrot do punktu nadawczego od 7,99 zł dla osób zapisanych do newslettera" color={TextColor.GRAYSCALE_600} fontSize={TextSize.XS} className="text-center mx-auto" textTransform="uppercase" />

                  <Text text="• możesz w każdej chwili zrezygnować z subskrypcji newslettera" color={TextColor.GRAYSCALE_600} fontSize={TextSize.XS} className="text-center mx-auto mb-4" textTransform="uppercase" />

                  <Text text="Ta strona jest chroniona przez reCAPTCHA. Dane osobowe zbierane i przetwarzane są w celu udzielenia odpowiedzi na skierowane przez formularz pytania." color={TextColor.GRAYSCALE_600} fontSize={TextSize.XS} className="max-w-[450px] text-center mx-auto" />
                </div>

                {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY} size="invisible" onChange={token_1 => handleRecaptchaChange(token_1, setFieldValue_0)} onExpired={handleRecaptchaExpired} onError={handleRecaptchaError} />}

                <ButtonsWrapper>
                  <Button type="submit" variant={ButtonVariant.SECONDARY} text={isSubmitting ? "Zapisywanie..." : "Zapisz się"} disabled={isSubmitting} loading={isSubmitting} />
                </ButtonsWrapper>
              </>}
          </FormWrapper>
        </NewsletterFormWrapper>}
    </Formik>;
};

// Styled components bez zmian
const CheckboxWrapper = styled.div`
  position: relative;

  .MuiFormControlLabel-label {
    width: fit-content;
  }
`;
const ErrorFormHelperText = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  color: ${({
  theme
}) => theme.colors.ERROR_500} !important;
  line-height: 20px;
  text-align: right !important;
`;
const InfoBar = styled.div`
  width: 100%;
  margin-bottom: 20px;
  text-align: center;
  padding: 2px 10px;

  ${({
  theme,
  $error
}) => $error && `
    background-color: ${theme.colors.ERROR_400};
  `}

  ${({
  theme,
  $success
}) => $success && `
    background-color: ${theme.colors.SPECIAL_300};
  `}
`;
const FormWrapper = styled(Form)`
  display: flex;
  flex-direction: column;

  .MuiFormGroup-root {
    margin-bottom: 0;
    margin-right: 16px;
    width: 100%;
  }

  button {
    width: 100%;
  }

  .MuiFormGroup-root {
    margin-bottom: 16px;
  }
`;
const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;

  button {
    width: fit-content;
    padding: 8px 32px;
  }

  ${({
  theme
}) => theme.mediaQueries.sm} {
    flex-direction: column-reverse;
    justify-content: center;
  }
`;
const NewsletterFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100% !important;
  max-width: 900px;
  background-color: ${({
  theme
}) => theme.colors.GRAYSCALE_50};

  ${({
  theme
}) => theme.mediaQueries.sm} {
    max-width: 100vw;
  }
`;
export default NewsletterForm;